import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

const OMA_CART_QUOTE_URL = 'https://omas-shipping.klikslim.workers.dev/api/cart-quote';

// OMA adresvalidatie: Cloudflare Worker verwerkt Google-verzoeken zonder browser-API-sleutel.
const OMA_ADDRESS_VALIDATION_URL = 'https://omas-adresvalidatie.klikslim.workers.dev/validate';

export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) {
        this.#onQuantityChange(event);
      }
    },
    300
  );

  #pendingCartFetch = null;

  // OMA: tweede klik bevestigt uitsluitend een onzeker adres met exact dezelfde invoer.
  #omaUncertainAddressSignature = null;

  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });

          this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
        } else if (external) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
        }
      });
  };

  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;

    event.promise
      ?.then(({ detail }) => {
        const sections = detail?.sections;
        const sectionsHtml = sections?.[this.sectionId];

        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Event promise rejected:', error);
        }
      });
  };

connectedCallback() {
  super.connectedCallback();

  this.#scheduleOmaShippingTotalUpdate();

  this.addEventListener(
    'click',
    this.#handleShippingCalendarClick
  );

  this.addEventListener(
    'click',
    this.#handleDeliveryAddressClick
  );

  this.addEventListener(
    'click',
    this.#handleCardMessageClick
  );

  this.addEventListener(
    'submit',
    this.#handleDeliveryAddressSubmit
  );

  this.addEventListener(
    'submit',
    this.#handleCardMessageSubmit
  );

  this.addEventListener(
    'input',
    this.#handleCardMessageInput
  );

  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    this.#handleCartUpdate
  );

  document.addEventListener(
    ThemeEvents.quantitySelectorUpdate,
    this.#debouncedOnChange
  );

  document.addEventListener(
    StandardEvents.cartDiscountUpdate,
    this.#handleDiscountUpdate
  );

  document.addEventListener(
    StandardEvents.cartNoteUpdate,
    this.#handleNoteUpdate
  );
}

disconnectedCallback() {
  super.disconnectedCallback();

  this.removeEventListener(
    'click',
    this.#handleShippingCalendarClick
  );

  this.removeEventListener(
    'click',
    this.#handleDeliveryAddressClick
  );

  this.removeEventListener(
    'click',
    this.#handleCardMessageClick
  );

  this.removeEventListener(
    'submit',
    this.#handleDeliveryAddressSubmit
  );

  this.removeEventListener(
    'submit',
    this.#handleCardMessageSubmit
  );

  this.removeEventListener(
    'input',
    this.#handleCardMessageInput
  );

  document.removeEventListener(
    StandardEvents.cartLinesUpdate,
    this.#handleCartUpdate
  );

  document.removeEventListener(
    ThemeEvents.quantitySelectorUpdate,
    this.#debouncedOnChange
  );

  document.removeEventListener(
    StandardEvents.cartDiscountUpdate,
    this.#handleDiscountUpdate
  );

  document.removeEventListener(
    StandardEvents.cartNoteUpdate,
    this.#handleNoteUpdate
  );
}

  // Toegevoegd: splitst een verhoging in de cart op in losse cartregels van quantity 1.
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      return;
    }

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    if (quantity > 1) {
      this.#addSeparateLineItems(line, quantity - 1);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 1,
      action: 'change',
    });
  }

  // Toegevoegd: kopieert het hoofdproduct met eigen bundle-ID en eigen gekoppelde add-ons.
  async #addSeparateLineItems(line, amount) {
    if (amount < 1) return;

    const marker = cartPerformance.createStartingMarker(
      'split-add:user-action'
    );

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = lineItemRow?.querySelector('text-component');

    textComponent?.shimmer();

    try {
      const cart = await this.fetchCartData();
      const sourceItem = cart.items?.[line - 1];

      if (!sourceItem) {
        throw new Error(
          'De geselecteerde cartregel kon niet worden gevonden.'
        );
      }

      const mainProperties = {
        ...(sourceItem.properties || {}),
      };

      delete mainProperties._separate_line_item_id;
      delete mainProperties._unique_line_item_id;
      delete mainProperties._separate_line_id;
      delete mainProperties._oma_card_variant_id;

      const sourceBundleId = mainProperties._oma_bundle_id;
      const sourceAddons = sourceBundleId
        ? cart.items.filter(
            (item) =>
              item.properties?._oma_addon === 'true' &&
              item.properties?._oma_bundle_id === sourceBundleId
          )
        : [];

      const createUniqueId = () =>
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}-${Math.random()
          .toString(36)
          .slice(2)}`;

      const sellingPlanId =
        sourceItem.selling_plan_allocation?.selling_plan?.id;

      const items = [];

      for (let index = 0; index < amount; index += 1) {
        const newBundleId = sourceBundleId
          ? createUniqueId()
          : null;

        const newMainProperties = newBundleId
          ? {
              _oma_bundle_id: newBundleId,
              ...mainProperties,
            }
          : {
              ...mainProperties,
            };

        if (newBundleId) {
          newMainProperties._oma_bundle_id = newBundleId;
        }

        const mainItem = {
          id: Number(sourceItem.variant_id),
          quantity: 1,
          properties: newMainProperties,
        };

        if (sellingPlanId) {
          mainItem.selling_plan = Number(sellingPlanId);
        }

        items.push(mainItem);

        if (!newBundleId) continue;

        for (const sourceAddon of sourceAddons) {
          const addonProperties = {
            ...(sourceAddon.properties || {}),
            _oma_addon: 'true',
            _oma_bundle_id: newBundleId,
          };

          delete addonProperties['Hoort bij'];
          delete addonProperties['Bericht op kaartje'];
          delete addonProperties['Tekst op het kaartje'];
          delete addonProperties._separate_line_item_id;
          delete addonProperties._unique_line_item_id;
          delete addonProperties._separate_line_id;

          const addonItem = {
            id: Number(sourceAddon.variant_id),
            quantity: sourceAddon.quantity || 1,
            properties: addonProperties,
          };

          const addonSellingPlanId =
            sourceAddon.selling_plan_allocation?.selling_plan?.id;

          if (addonSellingPlanId) {
            addonItem.selling_plan = Number(addonSellingPlanId);
          }

          items.push(addonItem);
        }
      }

      const sectionsToUpdate = new Set([this.sectionId]);

      document
        .querySelectorAll('cart-items-component')
        .forEach((component) => {
          if (
            component instanceof HTMLElement &&
            component.dataset.sectionId
          ) {
            sectionsToUpdate.add(component.dataset.sectionId);
          }
        });

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok || responseData.status) {
        throw new Error(
          responseData.description ||
            responseData.message ||
            'Het extra product kon niet worden toegevoegd.'
        );
      }

      const updatedCartResponse = await fetch(
        `${Theme.routes.cart_url}.json`,
        {
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
        }
      );

      if (!updatedCartResponse.ok) {
        throw new Error(
          'De bijgewerkte winkelwagen kon niet worden geladen.'
        );
      }

      const updatedCart = await updatedCartResponse.json();

      this.#updateCartBubble(updatedCart);

      resetShimmer(this);

      const sectionHtml = responseData.sections?.[this.sectionId];

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
    } catch (error) {
      console.error('Losse cartregel toevoegen mislukt:', error);

      resetShimmer(this);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Het extra product kon niet worden toegevoegd.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      const quantityInput =
        this.refs.quantitySelectors?.[line - 1]?.querySelector('input');

      if (quantityInput) {
        quantityInput.value = '1';
      }
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(marker);
    }
  }

  // Toegevoegd: verwijdert het hoofdproduct en alle gekoppelde OMA-add-ons in één cart-update.
  async onLineItemRemove(line) {
    const marker = cartPerformance.createStartingMarker(
      'remove-bundle:user-action'
    );

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    try {
      const cart = await this.fetchCartData();
      const sourceItem = cart.items?.[line - 1];

      if (!sourceItem) {
        throw new Error('De geselecteerde cartregel kon niet worden gevonden.');
      }

      const bundleId = sourceItem.properties?._oma_bundle_id;
      const changes = {
        [sourceItem.key]: 0,
      };

      if (bundleId) {
        for (const item of cart.items) {
          const isMatchingAddon =
            item.properties?._oma_addon === 'true' &&
            item.properties?._oma_bundle_id === bundleId;

          if (isMatchingAddon) {
            changes[item.key] = 0;
          }
        }
      }

      const sectionsToUpdate = new Set([this.sectionId]);

      document
        .querySelectorAll('cart-items-component')
        .forEach((component) => {
          if (
            component instanceof HTMLElement &&
            component.dataset.sectionId
          ) {
            sectionsToUpdate.add(component.dataset.sectionId);
          }
        });

      const body = JSON.stringify({
        updates: changes,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const lineIds = Object.keys(changes);

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: lineIds.map((id) => ({ id, quantity: 0 })),
          promise: deferredUpdatePromise.promise,
        })
      );

      const cartUpdateUrl =
        Theme.routes.cart_update_url ||
        `${window.Shopify?.routes?.root || '/'}cart/update.js`;

      const response = await fetch(
        cartUpdateUrl,
        fetchConfig('json', { body })
      );

      const responseData = await response.json();

      if (!response.ok || responseData.errors) {
        throw new Error(
          responseData.errors ||
            responseData.description ||
            'Het product kon niet worden verwijderd.'
        );
      }

      resetShimmer(this);
      this.#updateCartBubble(responseData);

      const mainItemCount = this.#getMainCartItemCount(responseData);
      const eventCartData = {
        ...responseData,
        item_count: mainItemCount,
      };

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(eventCartData),
        detail: {
          sections: responseData.sections,
          items: responseData.items,
          itemCount: mainItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      const sectionHtml = responseData.sections?.[this.sectionId];

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
    } catch (error) {
      console.error('Product en gekoppelde add-ons verwijderen mislukt:', error);

      resetShimmer(this);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Het product kon niet uit de winkelwagen worden verwijderd.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(marker);
    }
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(
        `${config.action}:user-action`
      );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents =
      document.querySelectorAll('cart-items-component');

    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise =
      CartLinesUpdateEvent.createPromise();

    const lineId =
      this.refs.cartItemRows[line - 1]?.dataset.key ?? '';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          config.action === 'change' && quantity > 0
            ? 'update'
            : 'remove',
        context: 'cart',
        lines: [
          {
            id: lineId,
            quantity,
          },
        ],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body })
    )
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);

          deferredUpdatePromise.reject(
            new Error(parsedResponseText.errors)
          );

          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount =
          newSectionHTML.querySelector(
            '[ref="cartItemCount"]'
          )?.textContent;

        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.#updateQuantitySelectors(parsedResponseText);
        this.#updateCartBubble(parsedResponseText);

        const mainItemCount = this.#getMainCartItemCount(parsedResponseText);
        const eventCartData = {
          ...parsedResponseText,
          item_count: mainItemCount,
        };

        deferredUpdatePromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              eventCartData
            ),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: mainItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          {
            mode: this.isDrawer ? 'hydration' : 'full',
          }
        );

        this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();

        cartPerformance.measureFromMarker(
          cartPerformaceUpdateMarker
        );
      });
  }

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector =
      this.refs.quantitySelectors[line - 1];

    const quantityInput =
      quantitySelector?.querySelector('input');

    if (!quantityInput) {
      throw new Error('Quantity input not found');
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError =
      this.refs[`cartItemError-${line}`];

    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) {
      throw new Error('Cart item error not found');
    }

    if (!(cartItemErrorContainer instanceof HTMLElement)) {
      throw new Error('Cart item error container not found');
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error:
          parsedResponseText.errors ||
          'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];

        const wasEmptyCartDrawer =
          this.isDrawer &&
          this.querySelector(
            '[data-cart-drawer-empty]'
          ) !== null;

        const mode =
          this.isDrawer ? 'hydration' : 'full';

        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(
            this.refs.cartItemRows?.map(
              (row) => row.dataset.key
            ) ?? []
          );

          if (wasEmptyCartDrawer) {
            startViewTransition(
              () => {
                morphSection(
                  this.sectionId,
                  cartItemsHtml,
                  morphOptions
                );
              },
              ['fill-cart-drawer']
            );
          } else {
            await morphSection(
              this.sectionId,
              cartItemsHtml,
              morphOptions
            );
          }

          if (
            !wasEmptyCartDrawer &&
            !prefersReducedMotion()
          ) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');

                onAnimationEnd(row, () => {
                  row.classList.remove('adding');
                });
              }
            }
          }

          this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            ...morphOptions,
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-items] Event promise rejected:',
            error
          );
        }
      });
  };


  // OMA: plant een nieuwe berekening van verzendkosten in nadat de cart/section is bijgewerkt.
  #scheduleOmaShippingTotalUpdate(delay = 0) {
    window.setTimeout(() => {
      this.#updateOmaShippingTotal().catch((error) => {
        console.warn('[oma-shipping] Cart totaal bijwerken mislukt:', error);
      });
    }, delay);
  }

  // OMA: formatteert centen als eurobedrag.
  #formatOmaMoney(cents, currency = 'EUR') {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(Number(cents || 0) / 100);
  }

  // OMA: werkt alle totalen op de cartpagina/cart drawer tegelijk bij.
  #renderOmaShippingTotals({
    subtotalCents,
    shippingCents = 0,
    currency = 'EUR',
    complete = false,
    loading = false,
    error = false,
  }) {
    const subtotalText = this.#formatOmaMoney(subtotalCents, currency);
    const totalText = this.#formatOmaMoney(
      subtotalCents + (complete ? shippingCents : 0),
      currency
    );

    document
      .querySelectorAll('[data-oma-cart-subtotal]')
      .forEach((element) => {
        element.textContent = subtotalText;
        element.dataset.subtotalCents = String(subtotalCents);
      });

    document
      .querySelectorAll('[data-oma-cart-shipping]')
      .forEach((element) => {
        if (loading) {
          element.textContent = 'Berekenen...';
          element.dataset.shippingState = 'loading';
          return;
        }

        if (error) {
          element.textContent = 'Kon niet worden berekend';
          element.dataset.shippingState = 'error';
          return;
        }

        if (!complete) {
          element.textContent = 'Vul alle bezorgadressen in';
          element.dataset.shippingState = 'incomplete';
          return;
        }

        element.textContent = this.#formatOmaMoney(shippingCents, currency);
        element.dataset.shippingState = 'complete';
        element.dataset.shippingCents = String(shippingCents);
      });

    document
      .querySelectorAll('[data-oma-cart-final-total]')
      .forEach((element) => {
        element.textContent = totalText;
        element.dataset.totalCents = String(
          subtotalCents + (complete ? shippingCents : 0)
        );
      });
  }

  // OMA: haalt de actuele cart op en vraagt dezelfde verzendprijs op als de shipping app gebruikt.
  async #updateOmaShippingTotal() {
    const hasTotals = document.querySelector(
      '[data-oma-cart-shipping], [data-oma-cart-final-total]'
    );

    if (!hasTotals) return;

    const cart = await this.fetchCartData();
    const subtotalCents = Number(cart?.total_price || 0);
    const currency = cart?.currency || 'EUR';
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const mainItems = items.filter(
      (item) => item.properties?._oma_addon !== 'true'
    );

    if (mainItems.length === 0) {
      this.#renderOmaShippingTotals({
        subtotalCents,
        currency,
        complete: false,
      });
      return;
    }

    const allAddressesComplete = mainItems.every((item) =>
      String(item.properties?.Bezorgadres || '').trim()
    );

    if (!allAddressesComplete) {
      this.#renderOmaShippingTotals({
        subtotalCents,
        currency,
        complete: false,
      });
      return;
    }

    this.#renderOmaShippingTotals({
      subtotalCents,
      currency,
      loading: true,
    });

    try {
      const response = await fetch(OMA_CART_QUOTE_URL, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          currency,
          items: items.map((item) => ({
            quantity: item.quantity,
            properties: item.properties || {},
          })),
        }),
      });

      const quote = await response.json().catch(() => null);

      if (!response.ok || !quote?.ok) {
        throw new Error(
          quote?.error ||
            `Verzendkosten endpoint gaf status ${response.status}.`
        );
      }

      const complete = quote.complete === true;
      const shippingCents = Number(quote.shippingCents || 0);

      this.#renderOmaShippingTotals({
        subtotalCents,
        shippingCents,
        currency: quote.currency || currency,
        complete,
      });
    } catch (error) {
      this.#renderOmaShippingTotals({
        subtotalCents,
        currency,
        error: true,
      });

      throw error;
    }
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();

      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector(
          'input[data-cart-quantity]'
        );

        if (!input) continue;

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        if (
          'updateCartQuantity' in selector &&
          typeof selector.updateCartQuantity === 'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      'cart-quantity-selector-component'
    )) {
      selector.updateButtonStates?.();
    }
  }

  // Toegevoegd: berekent het zichtbare aantal zonder OMA-add-ons.
  #getMainCartItemCount(cartData) {
    return Array.isArray(cartData?.items)
      ? cartData.items.reduce((total, item) => {
          if (item.properties?._oma_addon === 'true') {
            return total;
          }

          return total + Number(item.quantity || 0);
        }, 0)
      : Number(cartData?.item_count ?? cartData ?? 0);
  }

  // Toegevoegd: werkt de cartbubble bij met alleen hoofdproducten.
  #updateCartBubble(cartData) {
    const itemCount = this.#getMainCartItemCount(cartData);

    document
      .querySelectorAll('[ref="cartBubble"]')
      .forEach((bubble) => {
        bubble.classList.toggle(
          'visually-hidden',
          itemCount === 0
        );

        if (itemCount <= 99) {
          bubble.setAttribute(
            'data-maintain-ratio',
            ''
          );
        } else {
          bubble.removeAttribute(
            'data-maintain-ratio'
          );
        }
      });

    document
      .querySelectorAll('[ref="cartBubbleCount"]')
      .forEach((count) => {
        count.textContent =
          itemCount > 99
            ? '99+'
            : String(itemCount);

        count.classList.toggle(
          'hidden',
          itemCount === 0
        );
      });
  }

  // Toegevoegd: opent, sluit en bedient de popup voor het kaartbericht.
  #handleCardMessageClick = (event) => {
    const target =
      event.target instanceof Element ? event.target : null;

    if (!target) return;

    const addonRemoveButton = target.closest(
      '[data-oma-addon-remove]'
    );

    if (addonRemoveButton instanceof HTMLButtonElement) {
      const lineKey = addonRemoveButton.dataset.lineKey || '';

      if (lineKey) {
        this.#removeAddon(lineKey, addonRemoveButton);
      }

      return;
    }

    const toggleButton = target.closest(
      '[data-oma-card-message-toggle]'
    );

    if (toggleButton instanceof HTMLElement) {
      const row = toggleButton.closest(
        '[data-oma-card-message-row]'
      );

      if (!(row instanceof HTMLElement)) return;

      const isExpanded = row.classList.toggle('is-expanded');
      toggleButton.setAttribute(
        'aria-expanded',
        String(isExpanded)
      );
      toggleButton.setAttribute(
        'aria-label',
        isExpanded
          ? 'Bericht inklappen'
          : 'Volledig bericht tonen'
      );

      return;
    }

    const trigger = target.closest(
      '[data-oma-card-message-trigger]'
    );

    if (trigger instanceof HTMLElement) {
      const modal = this.querySelector(
        '[data-oma-card-message-modal]'
      );
      const form = modal?.querySelector(
        '[data-oma-card-message-form]'
      );

      if (
        !(modal instanceof HTMLElement) ||
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      const setField = (name, value) => {
        const field = form.elements.namedItem(name);

        if (
          field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement
        ) {
          field.value = value || '';
        }
      };

      form.reset();
      setField('line_key', trigger.dataset.lineKey);
      setField('bundle_id', trigger.dataset.bundleId);
      setField('card_line_key', trigger.dataset.cardLineKey);
      setField('card_variant_id', trigger.dataset.cardVariantId);
      setField(
        'main_product_title',
        trigger.dataset.mainProductTitle
      );
      setField('message', trigger.dataset.currentMessage);

      const messageInput = form.querySelector(
        'textarea[name="message"]'
      );

      if (messageInput instanceof HTMLTextAreaElement) {
        this.#updateCardMessageCounter(messageInput);
      }

      const title = modal.querySelector(
        '[data-oma-card-message-modal-title]'
      );

      if (title) {
        title.textContent = trigger.dataset.currentMessage
          ? 'Bericht op kaartje bewerken'
          : 'Kaartje toevoegen';
      }

      modal.hidden = false;
      document.body.classList.add('oma-card-modal-open');

      requestAnimationFrame(() => {
        form.querySelector('textarea[name="message"]')?.focus();
      });

      return;
    }

    if (target.closest('[data-oma-card-message-close]')) {
      this.#closeCardMessageModal();
    }
  };

  // Toegevoegd: houdt de resterende tekens in de kaartberichtpopup live bij.
  #handleCardMessageInput = (event) => {
    if (
      !(event.target instanceof HTMLTextAreaElement) ||
      !event.target.matches(
        '[data-oma-card-message-form] textarea[name="message"]'
      )
    ) {
      return;
    }

    this.#updateCardMessageCounter(event.target);
  };

  #updateCardMessageCounter(messageInput) {
    const form = messageInput.closest(
      '[data-oma-card-message-form]'
    );
    const counter = form?.querySelector(
      '[data-oma-card-message-counter]'
    );

    if (!counter) return;

    const maximumLength = 400;
    const remaining = Math.max(
      0,
      maximumLength - messageInput.value.length
    );

    counter.textContent = `${remaining} karakters over`;
  }

  // Toegevoegd: verwijdert één add-on en wist bij een kaartje ook het bericht van het hoofdproduct.
  async #removeAddon(lineKey, removeButton) {
    const marker = cartPerformance.createStartingMarker(
      'remove-addon:user-action'
    );
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();
    removeButton.disabled = true;

    try {
      const cart = await this.fetchCartData();
      const addonItem = cart.items?.find(
        (item) => item.key === lineKey
      );

      if (!addonItem || addonItem.properties?._oma_addon !== 'true') {
        throw new Error('De gekozen extra optie kon niet worden gevonden.');
      }

      const bundleId = addonItem.properties?._oma_bundle_id || '';
      const isCardAddon =
        Number(addonItem.variant_id) === 54073058722135 ||
        Boolean(addonItem.properties?.['Bericht op kaartje']) ||
        Boolean(addonItem.properties?.['Tekst op het kaartje']);

      if (isCardAddon && bundleId) {
        const mainItem = cart.items?.find(
          (item) =>
            item.properties?._oma_bundle_id === bundleId &&
            item.properties?._oma_addon !== 'true'
        );

        if (mainItem) {
          const mainProperties = {
            ...(mainItem.properties || {}),
          };

          delete mainProperties['Bericht op kaartje'];
          delete mainProperties['Tekst op het kaartje'];
          delete mainProperties['Kaartje erbij schrijven'];
          delete mainProperties._oma_card_variant_id;

          const mainResponse = await fetch(
            Theme.routes.cart_change_url,
            fetchConfig('json', {
              body: JSON.stringify({
                id: mainItem.key,
                quantity: mainItem.quantity,
                properties: mainProperties,
              }),
            })
          );

          const mainResponseData = await mainResponse.json();

          if (!mainResponse.ok || mainResponseData.errors) {
            throw new Error(
              mainResponseData.errors ||
                'Het kaartbericht kon niet worden verwijderd.'
            );
          }
        }
      }

      const sectionsToUpdate = new Set([this.sectionId]);

      document
        .querySelectorAll('cart-items-component')
        .forEach((component) => {
          if (
            component instanceof HTMLElement &&
            component.dataset.sectionId
          ) {
            sectionsToUpdate.add(component.dataset.sectionId);
          }
        });

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: [{ id: lineKey, quantity: 0 }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );

      const responseData = await response.json();

      if (!response.ok || responseData.errors) {
        throw new Error(
          responseData.errors ||
            'De extra optie kon niet worden verwijderd.'
        );
      }

      resetShimmer(this);
      this.#updateCartBubble(responseData);

      const mainItemCount = this.#getMainCartItemCount(responseData);
      const eventCartData = {
        ...responseData,
        item_count: mainItemCount,
      };

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(eventCartData),
        detail: {
          sections: responseData.sections,
          items: responseData.items,
          itemCount: mainItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      const sectionHtml = responseData.sections?.[this.sectionId];

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
    } catch (error) {
      console.error('Extra optie verwijderen mislukt:', error);
      resetShimmer(this);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'De extra optie kon niet worden verwijderd.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#enableCartItems();

      if (removeButton.isConnected) {
        removeButton.disabled = false;
      }

      cartPerformance.measureFromMarker(marker);
    }
  }

  // Toegevoegd: slaat een gewijzigd bericht op of voegt een betaald kaartjesproduct toe.
  #handleCardMessageSubmit = async (event) => {
    if (
      !(event.target instanceof HTMLFormElement) ||
      !event.target.matches('[data-oma-card-message-form]')
    ) {
      return;
    }

    event.preventDefault();

    const form = event.target;

    if (!form.reportValidity()) return;

    const formData = new FormData(form);
    const lineKey = String(formData.get('line_key') || '');
    const bundleId = String(formData.get('bundle_id') || '');
    const cardLineKey = String(formData.get('card_line_key') || '');
    const cardVariantId = Number(
      formData.get('card_variant_id') || 0
    );
    const mainProductTitle = String(
      formData.get('main_product_title') || ''
    );
    const message = String(formData.get('message') || '').trim();

    if (!lineKey || !bundleId || !message) return;

    const submitButton = form.querySelector(
      '.oma-card-modal__submit'
    );

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    try {
      const cart = await this.fetchCartData();
      const mainItem = cart.items?.find(
        (item) => item.key === lineKey
      );

      if (!mainItem) {
        throw new Error('De taart kon niet worden gevonden.');
      }

      const mainProperties = {
        ...(mainItem.properties || {}),
        'Bericht op kaartje': message,
      };

      delete mainProperties['Tekst op het kaartje'];
      delete mainProperties['Kaartje erbij schrijven'];
      delete mainProperties._oma_card_variant_id;

      const mainResponse = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: lineKey,
            quantity: mainItem.quantity,
            properties: mainProperties,
          }),
        })
      );

      const mainResponseData = await mainResponse.json();

      if (!mainResponse.ok || mainResponseData.errors) {
        throw new Error(
          mainResponseData.errors ||
            'Het bericht kon niet worden opgeslagen.'
        );
      }

      const sectionsToUpdate = new Set([this.sectionId]);

      document
        .querySelectorAll('cart-items-component')
        .forEach((component) => {
          if (
            component instanceof HTMLElement &&
            component.dataset.sectionId
          ) {
            sectionsToUpdate.add(component.dataset.sectionId);
          }
        });

      let finalResponseData;

      if (cardLineKey) {
        const cardItem = cart.items?.find(
          (item) => item.key === cardLineKey
        );

        if (!cardItem) {
          throw new Error('Het kaartjesproduct kon niet worden gevonden.');
        }

        const cardProperties = {
          ...(cardItem.properties || {}),
          _oma_bundle_id: bundleId,
          _oma_addon: 'true',
        };

        delete cardProperties['Hoort bij'];
        delete cardProperties['Bericht op kaartje'];
        delete cardProperties['Tekst op het kaartje'];
        delete cardProperties._separate_line_item_id;
        delete cardProperties._oma_card;

        const cardResponse = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', {
            body: JSON.stringify({
              id: cardLineKey,
              quantity: cardItem.quantity,
              properties: cardProperties,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            }),
          })
        );

        finalResponseData = await cardResponse.json();

        if (!cardResponse.ok || finalResponseData.errors) {
          throw new Error(
            finalResponseData.errors ||
              'Het kaartbericht kon niet worden bijgewerkt.'
          );
        }
      } else {
        if (!cardVariantId) {
          throw new Error(
            'Het kaartjesproduct heeft geen geldige variant.'
          );
        }

        const cardResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: cardVariantId,
                quantity: 1,
                properties: {
                  _oma_bundle_id: bundleId,
                  _oma_addon: 'true',
                },
              },
            ],
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        });

        finalResponseData = await cardResponse.json();

        if (!cardResponse.ok || finalResponseData.status) {
          throw new Error(
            finalResponseData.description ||
              finalResponseData.message ||
              'Het kaartje kon niet worden toegevoegd.'
          );
        }
      }

      const updatedCart = await this.fetchCartData();
      this.#updateCartBubble(updatedCart);
      resetShimmer(this);
      this.#closeCardMessageModal();

      const sectionHtml = finalResponseData.sections?.[this.sectionId];

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleOmaShippingTotalUpdate();
    } catch (error) {
      console.error('Kaartbericht opslaan mislukt:', error);
      resetShimmer(this);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Het kaartbericht kon niet worden opgeslagen.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#enableCartItems();

      if (
        submitButton instanceof HTMLButtonElement &&
        submitButton.isConnected
      ) {
        submitButton.disabled = false;
      }
    }
  };

  // Toegevoegd: sluit de popup voor het kaartbericht.
  #closeCardMessageModal() {
    const modal = this.querySelector(
      '[data-oma-card-message-modal]'
    );

    if (modal instanceof HTMLElement) {
      modal.hidden = true;
    }

    document.body.classList.remove('oma-card-modal-open');
  }

  // Toegevoegd: opent en sluit de bezorgadrespopup per cartregel.
#handleDeliveryAddressClick = (event) => {
  const target =
    event.target instanceof Element ? event.target : null;

  if (!target) return;

  const openButton = target.closest(
    '[data-delivery-address-trigger]'
  );

  if (openButton instanceof HTMLElement) {
    const modal = this.querySelector(
      '[data-delivery-address-modal]'
    );

    if (!(modal instanceof HTMLElement)) return;

    const form = modal.querySelector(
      '[data-delivery-address-form]'
    );

    const lineKeyInput = modal.querySelector(
      '[data-delivery-line-key]'
    );

 form.reset();
 this.#omaUncertainAddressSignature = null;
 this.#showOmaAddressValidationMessage(form, '');

 const countrySelect = form.elements.namedItem('country');

if (countrySelect instanceof HTMLSelectElement) {
  // Verwijder lege / --- placeholder-optie volledig
  Array.from(countrySelect.options).forEach((option) => {
    const text = option.textContent.trim();

    if (!option.value || text === '---') {
      option.remove();
    }
  });

  // Nederland standaard selecteren
  const netherlandsOption = Array.from(countrySelect.options).find(
    (option) =>
      option.value === 'Netherlands' ||
      option.textContent.trim() === 'Nederland'
  );

  if (netherlandsOption) {
    countrySelect.value = netherlandsOption.value;
  }
}

if (lineKeyInput instanceof HTMLInputElement) {
  countrySelect.value = 'Netherlands';
}

if (lineKeyInput instanceof HTMLInputElement) {
      lineKeyInput.value =
        openButton.dataset.lineKey || '';
    }

    const currentAddress =
      openButton.dataset.currentAddress || '';

    if (currentAddress) {
      this.#fillDeliveryAddressForm(
        form,
        currentAddress
      );
    }

    modal.hidden = false;

    document.body.classList.add(
      'delivery-address-modal-open'
    );

    requestAnimationFrame(() => {
      form
        .querySelector('input[name="name"]')
        ?.focus();
    });

    return;
  }

  const closeButton = target.closest(
    '[data-delivery-address-close]'
  );

  if (closeButton) {
    this.#closeDeliveryAddressModal();
  }
};

 // OMA adresvalidatie: melding alleen binnen de bestaande adrespopup.
 #showOmaAddressValidationMessage(form, message, isWarning = false) {
   const box = form.querySelector('[data-oma-address-validation-message]');
   if (!box) return;
   box.textContent = message;
   box.hidden = !message;
   box.style.color = isWarning ? '#805d15' : '#b42318';
 }

 // OMA adresvalidatie: vraag de beveiligde Worker om advies, zonder invoer te wijzigen.
 async #validateOmaAddress(form, { country, postcode, houseNumber, street, city }) {
   const controller = new AbortController();
   const timeout = window.setTimeout(() => controller.abort(), 5500);

   try {
     const response = await fetch(OMA_ADDRESS_VALIDATION_URL, {
       method: 'POST',
       mode: 'cors',
       credentials: 'omit',
       headers: {
         'Content-Type': 'application/json',
         Accept: 'application/json',
       },
       body: JSON.stringify({
         country,
         postcode,
         house_number: houseNumber,
         street,
         city,
       }),
       signal: controller.signal,
     });

     // OMA: HTTP- of Worker-storing => stille fallback, gewoon opslaan.
     if (!response.ok) return { status: 'unavailable' };

     const result = await response.json();
     if (!result?.ok) return { status: 'unavailable' };

     // OMA: uitsluitend expliciete validatieresultaten mogen opslaan blokkeren.
     if (result.status === 'invalid') return { status: 'invalid' };
     if (result.status === 'uncertain') return { status: 'uncertain' };
     return { status: 'valid' };
   } finally {
     window.clearTimeout(timeout);
   }
 }

// Toegevoegd: verwerkt het bezorgadresformulier.
#handleDeliveryAddressSubmit = async (event) => {
  if (!(event.target instanceof HTMLFormElement)) {
    return;
  }

  if (
    !event.target.matches(
      '[data-delivery-address-form]'
    )
  ) {
    return;
  }

  event.preventDefault();

  const form = event.target;

  if (!form.reportValidity()) return;

  const formData = new FormData(form);

  const lineKey = String(
    formData.get('line_key') || ''
  );

  if (!lineKey) return;

  const name = String(
    formData.get('name') || ''
  ).trim();

  const company = String(
    formData.get('company') || ''
  ).trim();

  const country = String(
    formData.get('country') || ''
  ).trim();

  const postcode = String(
    formData.get('postcode') || ''
  ).trim();

  const houseNumber = String(
    formData.get('house_number') || ''
  ).trim();

  const street = String(
    formData.get('street') || ''
  ).trim();

  const city = String(
    formData.get('city') || ''
  ).trim();

 // OMA: controleer uitsluitend of opslaan mag; verander geen veldwaarde of output.
 const addressSignature = JSON.stringify([country, postcode, houseNumber, street, city]);
 const submitButton = form.querySelector('.cart-delivery-modal__submit');
 this.#showOmaAddressValidationMessage(form, '');

 if (this.#omaUncertainAddressSignature !== addressSignature) {
   this.#omaUncertainAddressSignature = null;
   if (submitButton) submitButton.disabled = true;
   try {
     const validation = await this.#validateOmaAddress(form, {
       country, postcode, houseNumber, street, city,
     });
     if (validation.status === 'invalid') {
       this.#showOmaAddressValidationMessage(
         form, 'Dit adres lijkt niet te kloppen. Controleer de straat, het huisnummer, de postcode en de plaats.'
       );
       return;
     }
     if (validation.status === 'uncertain') {
       this.#omaUncertainAddressSignature = addressSignature;
       this.#showOmaAddressValidationMessage(
         form,
         'We kunnen dit adres niet helemaal controleren. Kijk de gegevens nog even na. Klopt alles? Klik dan nogmaals op Adres toevoegen.',
         true
       );
       return;
     }
   } catch (error) {
     // OMA: Google onbereikbaar, quotum/credits op of API-fout:
     // stilzwijgend doorgaan met de bestaande adresopslag.
     console.warn('[oma-address-validation] Controle overgeslagen:', error);
   } finally {
     if (submitButton?.isConnected) submitButton.disabled = false;
   }
 }
 this.#omaUncertainAddressSignature = null;

const countryNames = {
  Netherlands: 'Nederland',
  Belgium: 'België',
  Germany: 'Duitsland',
  France: 'Frankrijk',
  Luxembourg: 'Luxemburg',
};

const displayCountry =
  countryNames[country] || country;

const recipientLine = company
  ? `${name} - ${company}`
  : name;

const deliveryAddress = [
  recipientLine,
  `${street} ${houseNumber}`.trim(),
  `${postcode}, ${city}`.trim(),
  displayCountry,
]
  .filter(Boolean)
  .join('\n');

  await this.#saveDeliveryAddress(
    lineKey,
    deliveryAddress
  );
};

// Toegevoegd: slaat het bezorgadres op als line item property.
async #saveDeliveryAddress(
  lineKey,
  deliveryAddress
) {
  const modal = this.querySelector(
    '[data-delivery-address-modal]'
  );

  const submitButton = modal?.querySelector(
    '.cart-delivery-modal__submit'
  );

  if (submitButton instanceof HTMLButtonElement) {
    submitButton.disabled = true;
  }

  try {
    const cart = await this.fetchCartData();

    const sourceItem = cart.items?.find(
      (item) => item.key === lineKey
    );

    if (!sourceItem) {
      throw new Error(
        'De cartregel kon niet worden gevonden.'
      );
    }

    const properties = {
      ...(sourceItem.properties || {}),
      Bezorgadres: deliveryAddress,
    };

    const sectionsToUpdate = new Set([
      this.sectionId,
    ]);

    document
      .querySelectorAll(
        'cart-items-component'
      )
      .forEach((component) => {
        if (
          component instanceof HTMLElement &&
          component.dataset.sectionId
        ) {
          sectionsToUpdate.add(
            component.dataset.sectionId
          );
        }
      });

    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: lineKey,
          quantity: sourceItem.quantity,
          properties,
          sections: Array.from(
            sectionsToUpdate
          ).join(','),
          sections_url:
            window.location.pathname,
        }),
      })
    );

    const responseData =
      await response.json();

    if (
      !response.ok ||
      responseData.errors
    ) {
      throw new Error(
        responseData.errors ||
          'Het bezorgadres kon niet worden opgeslagen.'
      );
    }

    this.#closeDeliveryAddressModal();

    const sectionHtml =
      responseData.sections?.[
        this.sectionId
      ];

    if (sectionHtml) {
      await morphSection(
        this.sectionId,
        sectionHtml,
        {
          mode: this.isDrawer
            ? 'hydration'
            : 'full',
        }
      );
    } else {
      await sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
          mode: this.isDrawer
            ? 'hydration'
            : 'full',
        }
      );
    }

    this.#scheduleOmaShippingTotalUpdate();
  } catch (error) {
    console.error(
      'Bezorgadres opslaan mislukt:',
      error
    );
  } finally {
    if (
      submitButton instanceof HTMLButtonElement &&
      submitButton.isConnected
    ) {
      submitButton.disabled = false;
    }
  }
}

// Toegevoegd: sluit de bezorgadrespopup.
#closeDeliveryAddressModal() {
  const modal = this.querySelector(
    '[data-delivery-address-modal]'
  );

  if (modal instanceof HTMLElement) {
    modal.hidden = true;
  }

  document.body.classList.remove(
    'delivery-address-modal-open'
  );
}

// Toegevoegd: vult een bestaand bezorgadres opnieuw in.
// Toegevoegd: vult een bestaand bezorgadres opnieuw in.
#fillDeliveryAddressForm(
  form,
  deliveryAddress
) {
  const lines = deliveryAddress
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const setValue = (name, value) => {
    const field =
      form.elements.namedItem(name);

    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLSelectElement
    ) {
      field.value = value || '';
    }
  };

  if (lines.length < 4) return;

  const recipientLine = lines[0] || '';
  const streetLine = lines[1] || '';
  const postcodeCityLine = lines[2] || '';
  const countryLine = lines[3] || 'Nederland';

  const recipientParts =
    recipientLine.split(' - ');

  const name =
    recipientParts.shift()?.trim() || '';

  const company =
    recipientParts.join(' - ').trim();

  setValue('name', name);
  setValue('company', company);

  this.#splitStreetAndHouseNumber(
    form,
    streetLine
  );

  const postcodeCityMatch =
    postcodeCityLine.match(
      /^([^,]+),\s*(.+)$/
    );

  setValue(
    'postcode',
    postcodeCityMatch
      ? postcodeCityMatch[1].trim()
      : ''
  );

  setValue(
    'city',
    postcodeCityMatch
      ? postcodeCityMatch[2].trim()
      : postcodeCityLine
  );

  const countryValues = {
    Nederland: 'Netherlands',
    België: 'Belgium',
    Duitsland: 'Germany',
    Frankrijk: 'France',
    Luxemburg: 'Luxembourg',
  };

  setValue(
    'country',
    countryValues[countryLine] ||
      countryLine
  );
}

// Toegevoegd: splitst straat en huisnummer bij wijzigen.
#splitStreetAndHouseNumber(
  form,
  value
) {
  const match = value.match(
    /^(.*?)[,\s]+(\d+\S*)$/
  );

  const street =
    match ? match[1] : value;

  const houseNumber =
    match ? match[2] : '';

  const streetField =
    form.elements.namedItem('street');

  const numberField =
    form.elements.namedItem(
      'house_number'
    );

  if (
    streetField instanceof HTMLInputElement
  ) {
    streetField.value = street;
  }

  if (
    numberField instanceof HTMLInputElement
  ) {
    numberField.value = houseNumber;
  }
}

  // Toegevoegd: opent, rendert en beheert de verzendkalender per cartregel.
  #handleShippingCalendarClick = async (event) => {
    const target =
      event.target instanceof Element ? event.target : null;

    if (!target) return;

    const picker = target.closest(
      '[data-shipping-date-picker]'
    );

    if (!(picker instanceof HTMLElement)) return;

    const trigger = target.closest(
      '[data-shipping-calendar-trigger]'
    );

    if (trigger) {
      const calendar = picker.querySelector(
        '[data-shipping-calendar]'
      );

      if (!(calendar instanceof HTMLElement)) return;

      const shouldOpen = calendar.hidden;

      this.querySelectorAll(
        '[data-shipping-calendar]'
      ).forEach((otherCalendar) => {
        if (otherCalendar !== calendar) {
          otherCalendar.hidden = true;
        }
      });

      this.querySelectorAll(
        '[data-shipping-calendar-trigger]'
      ).forEach((otherTrigger) => {
        if (otherTrigger !== trigger) {
          otherTrigger.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      });

      calendar.hidden = !shouldOpen;
      trigger.setAttribute(
        'aria-expanded',
        String(shouldOpen)
      );

      if (shouldOpen) {
        const selectedDate =
          picker.dataset.selectedDate || '';

        const initialMonth = selectedDate
          ? this.#parseDate(selectedDate)
          : this.#getAmsterdamDate();

        picker.dataset.calendarYear = String(
          initialMonth.getFullYear()
        );

        picker.dataset.calendarMonth = String(
          initialMonth.getMonth()
        );

        this.#renderShippingCalendar(picker);
      }

      return;
    }

    const previousButton = target.closest(
      '[data-calendar-previous]'
    );

    if (previousButton) {
      this.#changeShippingCalendarMonth(picker, -1);
      return;
    }

    const nextButton = target.closest(
      '[data-calendar-next]'
    );

    if (nextButton) {
      this.#changeShippingCalendarMonth(picker, 1);
      return;
    }

    const dayButton = target.closest(
      '[data-shipping-date]'
    );

    if (
      dayButton instanceof HTMLButtonElement &&
      !dayButton.disabled
    ) {
      const selectedDate =
        dayButton.dataset.shippingDate;

      if (!selectedDate) return;

      await this.#saveShippingDate(
        picker,
        selectedDate
      );
    }
  };

  // Toegevoegd: rendert één maand van de verzendkalender.
  #renderShippingCalendar(picker) {
    const calendar = picker.querySelector(
      '[data-shipping-calendar]'
    );

    if (!(calendar instanceof HTMLElement)) return;

    const year = Number(
      picker.dataset.calendarYear
    );

    const month = Number(
      picker.dataset.calendarMonth
    );

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month)
    ) {
      return;
    }

    const monthDate = new Date(
      year,
      month,
      1,
      12,
      0,
      0
    );

    const monthLabel =
      new Intl.DateTimeFormat('nl-NL', {
        month: 'long',
        year: 'numeric',
        timeZone: 'Europe/Amsterdam',
      }).format(monthDate);

    const weekdayLabels = [
      'Ma',
      'Di',
      'Wo',
      'Do',
      'Vr',
      'Za',
      'Zo',
    ];

    const firstWeekday =
      (monthDate.getDay() + 6) % 7;

    const daysInMonth = new Date(
      year,
      month + 1,
      0
    ).getDate();

    const selectedDate =
      picker.dataset.selectedDate || '';

    const todayString =
      this.#formatDate(this.#getAmsterdamDate());

    let daysHtml = '';

    for (let index = 0; index < firstWeekday; index += 1) {
      daysHtml += `
        <span
          class="cart-shipping-calendar__day cart-shipping-calendar__day--empty"
          aria-hidden="true"
        ></span>
      `;
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(
        year,
        month,
        day,
        12,
        0,
        0
      );

      const dateString = this.#formatDate(date);
      const available =
        this.#isShippingDateAvailable(date);

      const selectedClass =
        dateString === selectedDate
          ? ' is-selected'
          : '';

      const todayClass =
        dateString === todayString
          ? ' is-today'
          : '';

      daysHtml += `
        <button
          type="button"
          class="cart-shipping-calendar__day${selectedClass}${todayClass}"
          data-shipping-date="${dateString}"
          aria-label="${date.toLocaleDateString(
            'nl-NL',
            {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }
          )}"
          ${available ? '' : 'disabled'}
        >
          ${day}
        </button>
      `;
    }

    calendar.innerHTML = `
      <div class="cart-shipping-calendar__header">
        <button
          type="button"
          class="cart-shipping-calendar__nav"
          data-calendar-previous
          aria-label="Vorige maand"
        >
          ‹
        </button>

        <div class="cart-shipping-calendar__month">
          ${monthLabel}
        </div>

        <button
          type="button"
          class="cart-shipping-calendar__nav"
          data-calendar-next
          aria-label="Volgende maand"
        >
          ›
        </button>
      </div>

      <div class="cart-shipping-calendar__weekdays">
        ${weekdayLabels
          .map(
            (label) => `
              <span class="cart-shipping-calendar__weekday">
                ${label}
              </span>
            `
          )
          .join('')}
      </div>

      <div class="cart-shipping-calendar__days">
        ${daysHtml}
      </div>
    `;
  }

// Toegevoegd: bepaalt beschikbaarheid met voorrang voor datumuitzonderingen.
#isShippingDateAvailable(date) {
  const settings = this.#getShippingCalendarSettings();
  const exceptions = this.#getShippingCalendarExceptions();

  const dateString = this.#formatDate(date);

  const exception = exceptions.find(
    (item) => item.datum === dateString
  );

  const weekdayKeys = [
    'zondag',
    'maandag',
    'dinsdag',
    'woensdag',
    'donderdag',
    'vrijdag',
    'zaterdag',
  ];

  const weekdayKey = weekdayKeys[date.getDay()];

  const rawSetting = exception
    ? exception.beschikbaarheid
    : settings[weekdayKey];

  const setting = String(rawSetting || '')
    .trim()
    .toLowerCase();

  if (
    !setting ||
    setting.includes('niet beschikbaar')
  ) {
    return false;
  }

  const amsterdamNow = this.#getAmsterdamDate();
  const todayString = this.#formatDate(amsterdamNow);

  if (dateString < todayString) {
    return false;
  }

  // Cut-offtijd geldt alleen voor vandaag.
  if (dateString !== todayString) {
    return true;
  }

  const cutoffMatch = setting.match(
    /(\d{1,2}):(\d{2})/
  );

  if (!cutoffMatch) {
    return true;
  }

  const cutoffMinutes =
    Number(cutoffMatch[1]) * 60 +
    Number(cutoffMatch[2]);

  const nowParts = this.#getAmsterdamDateTimeParts();

  const currentMinutes =
    nowParts.hour * 60 + nowParts.minute;

  return currentMinutes < cutoffMinutes;
}

  
  // Toegevoegd: slaat de verzenddatum op als line item property.
  async #saveShippingDate(
    picker,
    selectedDate
  ) {
    const lineKey = picker.dataset.lineKey;

    if (!lineKey) return;

    picker.setAttribute('aria-busy', 'true');

    try {
      const cart = await this.fetchCartData();

      const sourceItem = cart.items?.find(
        (item) => item.key === lineKey
      );

      if (!sourceItem) {
        throw new Error(
          'De cartregel kon niet worden gevonden.'
        );
      }

      const properties = {
        ...(sourceItem.properties || {}),
        Verzenddatum: selectedDate,
      };

      const sectionsToUpdate = new Set([
        this.sectionId,
      ]);

      document
        .querySelectorAll(
          'cart-items-component'
        )
        .forEach((component) => {
          if (
            component instanceof HTMLElement &&
            component.dataset.sectionId
          ) {
            sectionsToUpdate.add(
              component.dataset.sectionId
            );
          }
        });

      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: lineKey,
            quantity: sourceItem.quantity,
            properties,
            sections:
              Array.from(
                sectionsToUpdate
              ).join(','),
            sections_url:
              window.location.pathname,
          }),
        })
      );

      const responseData =
        await response.json();

      if (
        !response.ok ||
        responseData.errors
      ) {
        throw new Error(
          responseData.errors ||
            'De verzenddatum kon niet worden opgeslagen.'
        );
      }

      picker.dataset.selectedDate =
        selectedDate;

      const label = picker.querySelector(
        '[data-shipping-date-label]'
      );

      if (label instanceof HTMLElement) {
  const [year, month, day] = selectedDate.split('-');

  label.textContent =
    year && month && day
      ? `${day}-${month}-${year}`
      : selectedDate;
}

      const calendar = picker.querySelector(
        '[data-shipping-calendar]'
      );

      if (calendar instanceof HTMLElement) {
        calendar.hidden = true;
      }

      const trigger = picker.querySelector(
        '[data-shipping-calendar-trigger]'
      );

      trigger?.setAttribute(
        'aria-expanded',
        'false'
      );

      this.#updateCartBubble(responseData.items ? responseData : cart);

      const sectionHtml =
        responseData.sections?.[
          this.sectionId
        ];

      if (sectionHtml) {
        setTimeout(() => {
          morphSection(
            this.sectionId,
            sectionHtml,
            {
              mode: this.isDrawer
                ? 'hydration'
                : 'full',
            }
          );
          this.#scheduleOmaShippingTotalUpdate();
        }, 350);
      }
} catch (error) {
  console.error(
    'Verzenddatum opslaan mislukt:',
    error
  );
} finally {
  picker.removeAttribute('aria-busy');
}
}

  // Toegevoegd: wijzigt de weergegeven kalendermaand.
  #changeShippingCalendarMonth(
    picker,
    change
  ) {
    const currentYear = Number(
      picker.dataset.calendarYear
    );

    const currentMonth = Number(
      picker.dataset.calendarMonth
    );

    const nextMonth = new Date(
      currentYear,
      currentMonth + change,
      1,
      12,
      0,
      0
    );

    picker.dataset.calendarYear = String(
      nextMonth.getFullYear()
    );

    picker.dataset.calendarMonth = String(
      nextMonth.getMonth()
    );

    this.#renderShippingCalendar(picker);
  }

  // Toegevoegd: leest de weekinstellingen uit het Liquid JSON-script.
  #getShippingCalendarSettings() {
    const settingsElement =
      document.querySelector(
        '[data-shipping-calendar-settings]'
      );

    if (
      !(
        settingsElement instanceof
        HTMLScriptElement
      )
    ) {
      return {};
    }

    try {
      return JSON.parse(
        settingsElement.textContent || '{}'
      );
    } catch (error) {
      console.error(
        'Verzendkalenderinstellingen zijn ongeldig:',
        error
      );

      return {};
    }
  }

// Toegevoegd: leest uitzonderingen voor specifieke datums uit Liquid.
#getShippingCalendarExceptions() {
  const exceptionsElement = document.querySelector(
    '[data-shipping-calendar-exceptions]'
  );

  if (!(exceptionsElement instanceof HTMLScriptElement)) {
    return [];
  }

  try {
    const exceptions = JSON.parse(
      exceptionsElement.textContent || '[]'
    );

    return Array.isArray(exceptions) ? exceptions : [];
  } catch (error) {
    console.error(
      'Verzendkalender-uitzonderingen zijn ongeldig:',
      error
    );

    return [];
  }
}

  // Toegevoegd: geeft de huidige datum in de Nederlandse tijdzone.
  #getAmsterdamDate() {
    const parts =
      this.#getAmsterdamDateTimeParts();

    return new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      12,
      0,
      0
    );
  }

  // Toegevoegd: geeft datum- en tijdonderdelen voor Europe/Amsterdam.
  #getAmsterdamDateTimeParts() {
    const formatter =
      new Intl.DateTimeFormat('nl-NL', {
        timeZone: 'Europe/Amsterdam',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });

    const parts = {};

    for (const part of formatter.formatToParts(
      new Date()
    )) {
      if (part.type !== 'literal') {
        parts[part.type] = Number(
          part.value
        );
      }
    }

    return parts;
  }

  // Toegevoegd: zet een datum om naar YYYY-MM-DD.
  #formatDate(date) {
    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');
    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Toegevoegd: leest YYYY-MM-DD zonder tijdzoneverschuiving.
  #parseDate(dateString) {
    const [year, month, day] =
      dateString.split('-').map(Number);

    return new Date(
      year,
      month - 1,
      day,
      12,
      0,
      0
    );
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) {
      return this.#pendingCartFetch;
    }

    this.#pendingCartFetch = (async () => {
      const response = await fetch(
        `${Theme.routes.cart_url}.json`,
        {
          headers: {
            Accept: 'application/json',
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch cart: ${response.status} ${response.statusText}`
        );
      }

      return response.json();
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) {
      throw new Error('Section id missing');
    }

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define(
    'cart-items-component',
    CartItemsComponent
  );
}